import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ScrollReveal from '../components/ScrollReveal';

export default function About() {
  const teamImage = "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/fyns-toemreren-firmabil-byggeplads.jpg";

  return (
    <>
      <SEO 
        title="Om os" 
        description="Læs om Fyns - Tømreren ApS i Munkebo. Vores historie, værdier og passion for kvalitetsbetonet tømrer- og snedkerarbejde på hele Fyn. Få et uforpligtende tilbud." 
        image={teamImage}
      />

      {/* Header */}
      <section className="bg-[#1F2A37] text-white py-16 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            Om Fyns - Tømreren ApS
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-base leading-relaxed">
            Mød din lokale tømrermester på Fyn – med passion for faglig kvalitet og tilfredse kunder.
          </p>
        </div>
      </section>

      {/* Main Presentation */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Team Photo */}
            <ScrollReveal animation="slide-left" duration={800} className="relative">
              <figure className="overflow-hidden shadow-xl border border-gray-100 bg-white">
                <img
                  src={teamImage}
                  alt="Fyns-Tømreren firmabil fotograferet på byggeplads i Munkebo på Fyn"
                  className="w-full h-[500px] object-cover"
                />
                <figcaption className="p-3.5 bg-[#F9FAFB] text-xs text-gray-600 border-t border-gray-200 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <i className="fa-solid fa-truck text-[#3A5A40]"></i>
                    <span>Fyns-Tømreren firmabil – professionelt udstyr på byggepladsen</span>
                  </span>
                  <span className="text-[11px] font-medium text-gray-500">Munkebo & hele Fyn</span>
                </figcaption>
              </figure>
            </ScrollReveal>

            {/* History & Presentation Text */}
            <ScrollReveal animation="slide-right" duration={800} className="space-y-6">
              <h2 className="text-3xl font-bold text-[#111111] leading-tight">
                Solidt håndværk forankret i Munkebo på Fyn
              </h2>
              <p className="text-gray-700 leading-relaxed text-base">
                <Link to="/" className="text-[#3A5A40] hover:text-[#2D4632] underline decoration-1 underline-offset-2 font-semibold" title="Gå til forsiden for Fyns Tømreren">Fyns - Tømreren ApS</Link> blev grundlagt ud fra et stærkt ønske om at levere ordentligt, gennemtænkt og holdbart tømrerarbejde til boligejere og erhvervsliv på Fyn.
              </p>
              <p className="text-gray-700 leading-relaxed text-base">
                Vi lægger afgørende vægt på den gode dialog. Når du arbejder sammen med os, oplever du en nærværende tømrermester, der lytter til dine ønsker, kommer med faglige indspark og holder dig opdateret undervejs i forløbet.
              </p>
              
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="text-[#3A5A40] text-base flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#111111] text-base">Kompromisløs kvalitet</h3>
                    <p className="text-gray-600 text-sm">Vi går aldrig på kompromis med materialer eller udførelse.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-[#3A5A40] text-base flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#111111] text-base">Tidsplaner og aftaler overholdes</h3>
                    <p className="text-gray-600 text-sm">En aftale er en aftale – både hvad angår pris og leveringstidspunkt.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-[#3A5A40] text-base flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#111111] text-base">Rent og ryddeligt arbejdssted</h3>
                    <p className="text-gray-600 text-sm">Vi passer på din bolig og rydder pænt op efter os hver dag.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-[#3A5A40] hover:bg-[#2D4632] text-white px-6 py-3 font-semibold text-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  Få et uforpligtende tilbud
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>

            </ScrollReveal>

          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <h2 className="text-3xl font-bold text-[#111111]">
              Vores værdier i dagligdagen
            </h2>
            <p className="text-gray-600 text-sm">
              Fire grundpiller der sikrer dig den bedste oplevelse.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 shadow-xs border border-gray-100 text-center space-y-4">
              <div className="text-[#3A5A40] text-3xl mx-auto">
                <i className="fa-solid fa-handshake"></i>
              </div>
              <h3 className="font-bold text-lg text-[#111111]">Troværdighed</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Åbenhed og gennemskuelige tilbud så du ved præcis, hvad du får.
              </p>
            </div>

            <div className="bg-white p-8 shadow-xs border border-gray-100 text-center space-y-4">
              <div className="text-[#3A5A40] text-3xl mx-auto">
                <i className="fa-solid fa-compass-drafting"></i>
              </div>
              <h3 className="font-bold text-lg text-[#111111]">Præcision</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Nøjagtige opmålinger og millimeterpræcis montering og samlinger.
              </p>
            </div>

            <div className="bg-white p-8 shadow-xs border border-gray-100 text-center space-y-4">
              <div className="text-[#3A5A40] text-3xl mx-auto">
                <i className="fa-solid fa-tree"></i>
              </div>
              <h3 className="font-bold text-lg text-[#111111]">Gode materialer</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Langtidsholdbart træ og godkendte byggekomponenter.
              </p>
            </div>

            <div className="bg-white p-8 shadow-xs border border-gray-100 text-center space-y-4">
              <div className="text-[#3A5A40] text-3xl mx-auto">
                <i className="fa-solid fa-heart"></i>
              </div>
              <h3 className="font-bold text-lg text-[#111111]">Mennesket i centrum</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                God og venlig betjening hver gang du taler med os.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* On-Site & Work Showcase */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-[#d75d5a]">Lokalt forankret på Fyn</span>
              <h2 className="text-3xl font-bold text-[#111111] leading-tight">
                Vi ruller ud til opgaver i hele regionen
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Med base i Munkebo servicerer vi hele Fyn – fra Odense og Kerteminde til Nyborg, Svendborg og Middelfart. Vores fuldt udstyrede firmabil sikrer, at vi altid møder op med de rette professionelle værktøjer og materialer til opgaven.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-[#F9FAFB] p-4 border border-gray-200">
                  <div className="text-2xl font-extrabold text-[#1F2A37]">100%</div>
                  <div className="text-xs text-gray-600 mt-1 font-medium">Dedikation til kvalitet</div>
                </div>
                <div className="bg-[#F9FAFB] p-4 border border-gray-200">
                  <div className="text-2xl font-extrabold text-[#1F2A37]">Hele Fyn</div>
                  <div className="text-xs text-gray-600 mt-1 font-medium">Lokal dækning & hurtig udrykning</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <figure className="overflow-hidden border border-gray-200 bg-white shadow-xs">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/fyns-toemreren-firmabil-byggeplads.jpg"
                    alt="Fyns-Tømreren firmabil fotograferet på byggeplads"
                    className="w-full h-44 sm:h-48 object-cover"
                  />
                  <figcaption className="p-2 bg-gray-50 text-[11px] text-gray-600 border-t border-gray-100">
                    Firmabil på byggeplads i Munkebo
                  </figcaption>
                </figure>
                <figure className="overflow-hidden border border-gray-200 bg-white shadow-xs">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/faerdigmonteret-traeterrasse-bolig.jpg"
                    alt="Nybygget træterrasse i kvalitetstræ ved privat bolig på Fyn"
                    className="w-full h-44 sm:h-48 object-cover"
                  />
                  <figcaption className="p-2 bg-gray-50 text-[11px] text-gray-600 border-t border-gray-100">
                    Færdigmonteret træterrasse
                  </figcaption>
                </figure>
              </div>
              <div className="space-y-4 pt-4 sm:pt-6">
                <figure className="overflow-hidden border border-gray-200 bg-white shadow-xs">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/toemrerarbejde-tag-laegtekonstruktion.jpg"
                    alt="Tømrer i arbejde på lægtekonstruktion under tagrenovering"
                    className="w-full h-44 sm:h-48 object-cover"
                  />
                  <figcaption className="p-2 bg-gray-50 text-[11px] text-gray-600 border-t border-gray-100">
                    Tømrerarbejde på tagkonstruktion
                  </figcaption>
                </figure>
                <figure className="overflow-hidden border border-gray-200 bg-white shadow-xs">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/tegltag-integrerede-solceller-parcelhus.jpg"
                    alt="Færdigt tegltag med solceller monteret på parcelhus"
                    className="w-full h-44 sm:h-48 object-cover"
                  />
                  <figcaption className="p-2 bg-gray-50 text-[11px] text-gray-600 border-t border-gray-100">
                    Tegltag med integrerede solceller
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1F2A37] text-white text-center">
        <div className="max-w-4xl mx-auto px-4 space-y-6">
          <h2 className="text-3xl font-bold">Lad os drøfte dit tømrerprojekt</h2>
          <p className="text-gray-300">
            Vi står altid klar med gode råd og et uforpligtende pristilbud.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-block bg-[#3A5A40] hover:bg-[#2D4632] text-white px-8 py-3 font-semibold text-base transition-colors"
            >
              Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
